import { SyncOutlined } from '@ant-design/icons';
import { Button, Drawer, Flex, message, Typography } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { downloadFile } from '@/utils/dataDownloadUtils';
import { useFileFetcher } from '@/services/queryUtils';
import { CONTENT_PERFORMANCE_PAGE } from '@/utils/constants';
import { mixpanelTrack } from '@/utils/mixpanelUtils/asyncMixpanel';
import { MIXPANEL_EVENT, RevenueDashboardWidget } from '@/utils/mixpanelUtils/events';
import {
  buildContentExportPath,
  formatDownloadButtonLabel,
} from '../DownloadDrawer/downloadDrawerUtils';
import type {
  ContentPerformanceDownloadDrawerProps,
  ContentPerformanceDownloadSettingsState,
  ContentPerformanceExportFileKey,
} from './contentPerformanceDownloadDrawer.d';
import DownloadShowDateFilters from '../DownloadDrawer/DownloadShowDateFilters';
import ContentPerformanceReportsSectionsEcho from './ContentPerformanceReportsSectionsEcho';

const buildDefaultInclude = (): ContentPerformanceDownloadSettingsState['include'] => ({
  'episode-totals': true,
  'episode-platforms': true,
  'launch-first-7': true,
  'launch-first-30': true,
});

/**
 * Seeds drawer settings from the page filters. An empty `selectedShows` means all shows
 * (same semantics as {@link FiltersMultiSelectEcho} on the main page).
 */
const buildDefaultDownloadSettings = (
  globalFilters: ContentPerformanceDownloadDrawerProps['globalFilters']
): ContentPerformanceDownloadSettingsState => ({
  shows: globalFilters.selectedShows,
  dateRange: globalFilters.dateRange,
  include: buildDefaultInclude(),
});

/**
 * Echo drawer for downloading content performance metrics as one or more CSV files (ZIP).
 *
 * @deprecated Legacy `files=` export drawer.
 * TODO: remove once `newDownloadDrawers` is enabled for everyone.
 */
const ContentPerformanceDownloadDrawerEcho = ({
  isOpen,
  handleClose,
  selectedNetworks,
  globalFilters,
  shows,
}: ContentPerformanceDownloadDrawerProps) => {
  const fetchFile = useFileFetcher();
  const defaultDownloadSettings = buildDefaultDownloadSettings(globalFilters);

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSettings, setDownloadSettings] =
    useState<ContentPerformanceDownloadSettingsState>(defaultDownloadSettings);

  const selectedFileCount = Object.values(downloadSettings.include).filter(Boolean).length;

  /**
   * @deprecated Legacy `files=` drawer date tracking.
   * TODO: remove once `newDownloadDrawers` is enabled for everyone.
   */
  const onDateOpen = useCallback(() => {
    mixpanelTrack(MIXPANEL_EVENT.contentPerformance.downloadDrawer.dateOpened, {
      widget: RevenueDashboardWidget.ContentPerformanceDownloadDrawer,
      page: CONTENT_PERFORMANCE_PAGE,
    });
  }, []);

  const onDatePresetSelect = useCallback((preset: string) => {
    mixpanelTrack(MIXPANEL_EVENT.contentPerformance.downloadDrawer.datePresetSelected, {
      widget: RevenueDashboardWidget.ContentPerformanceDownloadDrawer,
      page: CONTENT_PERFORMANCE_PAGE,
      preset,
    });
  }, []);

  const onDateApply = useCallback((from: string, to: string) => {
    mixpanelTrack(MIXPANEL_EVENT.contentPerformance.downloadDrawer.dateApplied, {
      widget: RevenueDashboardWidget.ContentPerformanceDownloadDrawer,
      page: CONTENT_PERFORMANCE_PAGE,
      start: from,
      end: to,
    });
  }, []);

  const wasDrawerOpenForSettingsSeedRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasDrawerOpenForSettingsSeedRef.current) {
      setDownloadSettings(buildDefaultDownloadSettings(globalFilters));
    }
    wasDrawerOpenForSettingsSeedRef.current = isOpen;
  }, [isOpen, globalFilters]);

  const handleDownload = async () => {
    const isAllShowsSelected = downloadSettings.shows.length === 0;
    const networksWithIds = selectedNetworks.filter(
      (network): network is typeof network & { id: string } => Boolean(network.id)
    );
    const networkIds = networksWithIds.map((network) => network.id);
    const networkNames = networksWithIds.map((network) => network.name ?? '');
    const includeLaunchFirst7 = downloadSettings.include['launch-first-7'];
    const includeLaunchFirst30 = downloadSettings.include['launch-first-30'];
    const mixPanelBase = {
      networkIds,
      networkNames,
      shows: isAllShowsSelected ? 'all' : downloadSettings.shows,
      start: downloadSettings.dateRange.from,
      end: downloadSettings.dateRange.to,
      includeEpisodeTotals: downloadSettings.include['episode-totals'],
      includeEpisodePlatforms: downloadSettings.include['episode-platforms'],
      includeLaunchFirst7,
      includeLaunchFirst30,
    };

    setIsDownloading(true);

    try {
      const { from, to } = downloadSettings.dateRange;

      const fileKeys = (
        Object.entries(downloadSettings.include) as [ContentPerformanceExportFileKey, boolean][]
      )
        .filter(([, enabled]) => enabled)
        .map(([key]) => key);

      if (fileKeys.length === 0 || networkIds.length === 0) {
        message.error('No data to download');
        return;
      }

      const path = buildContentExportPath({
        networkIds,
        from,
        to,
        fileKeys,
        showIds: downloadSettings.shows,
      });
      const { blob, fileName } = await fetchFile(path);
      downloadFile(blob, fileName);

      mixpanelTrack(MIXPANEL_EVENT.contentPerformance.downloadDrawer.downloadSuccess, {
        ...mixPanelBase,
        widget: RevenueDashboardWidget.ContentPerformanceDownloadDrawer,
        page: CONTENT_PERFORMANCE_PAGE,
      });
    } catch (error) {
      mixpanelTrack(MIXPANEL_EVENT.contentPerformance.downloadDrawer.downloadFailed, {
        ...mixPanelBase,
        errorCode: 'UNKNOWN_ERROR',
        widget: RevenueDashboardWidget.ContentPerformanceDownloadDrawer,
        page: CONTENT_PERFORMANCE_PAGE,
      });
      console.error('Error downloading content performance data', error);
      message.error('Failed to download data');
    } finally {
      setIsDownloading(false);
    }
  };

  const drawerFooter = (
    <Flex justify="flex-end" gap={12} style={{ width: '100%' }}>
      <Button danger type="text" onClick={handleClose}>
        Cancel
      </Button>
      <Button
        variant="outlined"
        color="primary"
        disabled={selectedFileCount === 0}
        onClick={handleDownload}
        loading={isDownloading ? { icon: <SyncOutlined spin /> } : false}
      >
        {formatDownloadButtonLabel(selectedFileCount)}
      </Button>
    </Flex>
  );

  if (!isOpen) return null;

  return (
    <Drawer
      open={isOpen}
      data-testid="content-performance-download-drawer"
      footer={drawerFooter}
      size="large"
      title={
        <Typography.Title level={3} style={{ margin: 0 }}>
          Download performance data (CSV)
        </Typography.Title>
      }
      onClose={handleClose}
    >
      <Flex vertical style={{ height: '100%' }}>
        <DownloadShowDateFilters
          shows={shows}
          downloadSettings={downloadSettings}
          setDownloadSettings={setDownloadSettings}
          onDateOpen={onDateOpen}
          onDatePresetSelect={onDatePresetSelect}
          onDateApply={onDateApply}
        />
        <ContentPerformanceReportsSectionsEcho
          downloadSettings={downloadSettings}
          setDownloadSettings={setDownloadSettings}
        />
      </Flex>
    </Drawer>
  );
};

export default ContentPerformanceDownloadDrawerEcho;
